import { useContext, useEffect, useMemo, useState } from "react";
import { Box, ClickAwayListener, IconButton, InputAdornment, MenuItem, Paper, Popper, TextField, Tooltip, Typography } from "@mui/material";
import { type MRT_ColumnDef } from "material-react-table";
import { formatCurrency, formatRelationshipType, RELATIONSHIP_TYPE_OPTIONS, relationshipTypeOptions } from "../../utils/utils";
import { DateRangeValue, Options, OrgManagementRow } from "../../utils/types";
import EditIcon from "../../assets/common/edit-icon.svg";
import { GlobalContext } from "../../context/GlobalContext";
import ColumnFilter from "../Common/customFilter";
import DescSortIcon from "../../assets/common/Desc-sort-icon.svg";
import AscSortIcon from "../../assets/common/Asc-sort-icon.svg";
import ActionItemsAssigneeCell from "../ActionItems/ActionItemsAssigneeCell";
import DateCellEditor from "../Common/DateCellEditor";

let activeEditorKey: string | null = null;
const activeEditorListeners = new Set<(key: string | null) => void>();

const setActiveEditorKey = (key: string | null) => {
  activeEditorKey = key;
  activeEditorListeners.forEach((listener) => listener(key));
};
const initialSort = { sort_field: "accountName", sort_order: "asc" as "asc" | "desc" };

interface InlineEditorProps {
  row: OrgManagementRow;
  field: "type" | "accountName" | "mrr" | "startDate" | "endDate" | "clientType";
  value: string;
  disabled?: boolean;
  error?: boolean;
  options?: readonly string[];
  onSave: (row: OrgManagementRow, field: "type" | "accountName" | "mrr" | "startDate" | "endDate" | "clientType", value: string) => void;
  onClearRelationshipType?: (row: OrgManagementRow) => void;
  clientTypeOptions?: Options[];
}

const InlineEditor = ({
  row,
  field,
  value,
  disabled = false,
  error = false,
  onSave,
  clientTypeOptions,
  // onClearRelationshipType,
}: InlineEditorProps) => {
  const [currentValue, setCurrentValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const [hovered, setHovered] = useState(false);
  const { accountDetails } = useContext(GlobalContext);
  const isAdmin = ["admin", "superaccess"].includes(accountDetails?.roleId?.toLowerCase() || "");
  const isMrrField = field === "mrr";
  const isRelationshipField = field === "type";
  const isAccountNameField = field === "accountName";
  const editorKey = `${row.id}-${field}`;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isClientTypeField = field === "clientType";

  const dropdownOptions = isRelationshipField
    ? RELATIONSHIP_TYPE_OPTIONS.map((item) => ({
        label: formatRelationshipType(item),
        value: item,
      }))
    : clientTypeOptions ?? [];

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    const handleActiveEditorChange = (key: string | null) => {
      const active = key === editorKey;
      setIsEditing(active);

      if (!active) {
        setCurrentValue(value);
        setHovered(false);
      } else if (isMrrField && value === "") {
        setCurrentValue("");
      }
    };

    activeEditorListeners.add(handleActiveEditorChange);
    return () => {
      activeEditorListeners.delete(handleActiveEditorChange);
      if (activeEditorKey === editorKey) {
        setActiveEditorKey(null);
      }
    };
  }, [editorKey, isMrrField, value]);

  const handleSave = () => {
    if (isMrrField && currentValue.trim() === "") {
      onSave(row, field, "0");
      setHovered(false);
      setActiveEditorKey(null);
      setAnchorEl(null);
      setCurrentValue("0");
      return;
    }

    if (currentValue.trim() === value.trim()) {
      setHovered(false);
      setActiveEditorKey(null);
      setAnchorEl(null);
      return;
    }

    onSave(row, field, currentValue);

    setHovered(false);
    setActiveEditorKey(null);
    setAnchorEl(null);
  };

  const handleCancelEdit = () => {
    setCurrentValue(value);
    setHovered(false);
    setActiveEditorKey(null);
  };

  const renderReadOnlyText = (text: string) => (
    <Box className="domain-management-value-box domain-management-chip-label">
      {text}
    </Box>
  );
  const hasValue =
    currentValue &&
    currentValue !== "Select" &&
    currentValue.trim() !== "";

  const renderReadOnlyRelationshipType = (text: string) => (
    <Box
      className="domain-management-chip"
      sx={{
        border: hasValue ? "1px solid #E0D5FF" : "1px solid #C2C2CA",
        cursor: "default",
        backgroundColor: hasValue ? "#F6F1FF" : "#FFFFF",
        width: "100%",
        maxWidth: isClientTypeField ? "120px":"90px",
        borderRadius: "999px",
        minHeight: "24px",
        paddingLeft: "20px",
        marginLeft: isClientTypeField ? "":"20px"
      }}
    >
      <Typography
        className="domain-management-chip-label"
        sx={{
          color: hasValue ? "#6036d6" : "#7A7A8C",
          fontWeight: 600,
        }}
      >
        {text || "-"}
      </Typography>
    </Box>
  );

  if (!isAdmin && (isRelationshipField || isClientTypeField)) {
    return renderReadOnlyRelationshipType(formatRelationshipType(value || "-"));
  }

  if (!isAdmin && isMrrField) {
    return renderReadOnlyText(
      formatCurrency(Number(value) || 0)
    );
  }

  if (!isAdmin || isAccountNameField) {
    return renderReadOnlyText(value || "-");
  }

  if (isRelationshipField || isClientTypeField) {
    return (
      <Box className="cell-hover-parent" sx={{ position: "relative", width: "100%" }}>
        <TextField
          select
          variant="outlined"
          size="small"
          fullWidth
          value={currentValue}
          disabled={disabled}
          error={error}
          onChange={(event) => {
            const nextValue = event.target.value;
            setCurrentValue(nextValue);
            onSave(row, field, nextValue);
          }}
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected: unknown) => {
              const rendered = typeof selected === "string" ? selected : "";
              return rendered
                ? formatRelationshipType(rendered)
                : "-";
            },
          }}
          sx={{
            width: "auto",
            minWidth: "120px",
            paddingLeft: isClientTypeField ? "":"20px",
            "& .MuiOutlinedInput-root": {
              minHeight: 28,
              backgroundColor: hasValue ? "#F6F1FF" : "#FFFFFF",
              borderRadius: "999px",
              paddingRight: "6px",
              paddingLeft: "12px",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: hasValue ? "#E0D5FF" : "#C2C2CA",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: hasValue ? "#CBB6FF" : "#C2C2CA",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: hasValue ? "#B892FF" : "#C2C2CA",
                borderWidth: 1,
              },
            },
            "& .MuiSelect-select": {
              color: hasValue ? "#6036d6" : "#7A7A8C",
              fontWeight: 600,
              paddingRight: "24px !important",
              display: "flex",
              alignItems: "center",
            },
            "& .MuiSelect-icon": {
              color: hasValue ? "#6036d6" : "#7A7A8C",
              right: 8,
            },
            "& .MuiInputBase-input": {
              py: 0,
            },
            "& .MuiOutlinedInput-input": {
              paddingLeft: "0px",
            },
          }}
        >
          {dropdownOptions.map((option) => {
            const isSelected = currentValue === option.value;
            return (
              <MenuItem
                key={option.value}
                value={option.value}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%",
                  }}
                >
                  <Typography>{option.label}</Typography>
                  {isClientTypeField && isSelected && (
                    <Box
                      className="clear-icon"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setCurrentValue("");
                        onSave(row, field, "");
                      }}
                    >
                      ×
                    </Box>
                  )}
                </Box>
              </MenuItem>
            );
          })}
        </TextField>
      </Box>
    );
  }

  if (isMrrField) {
    return (
      <>
        <Box
          className="domain-management-edit-box domain-management-chip domain-management-chip-dashed"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={(event) => {
            event.stopPropagation();

            if (!disabled) {
              setAnchorEl(event.currentTarget);
              setActiveEditorKey(editorKey);
            }
          }}
          sx={{width: "auto",
            ...(isEditing && {
              backgroundColor: "#F6F1FF",
              border: "1px dashed #B892FF",
            }),
          }}
        >
          <Box className="domain-management-chip-label">
            {formatCurrency(Number(currentValue) || 0)}
          </Box>

          {hovered && (
          <Tooltip title="Edit">
            <IconButton
              size="small"
              sx={{
                opacity: hovered ? 1 : 0,
                visibility: hovered ? "visible" : "hidden",
              }}
            >
              <img src={EditIcon} alt="Edit" width={14} height={14} />
            </IconButton>
          </Tooltip>
        )}
        </Box>

        <Popper
          open={isEditing}
          anchorEl={anchorEl}
          placement="bottom-start"
          sx={{ zIndex: 1500 }}
        >
          <ClickAwayListener
            onClickAway={() => {
              handleCancelEdit();
              setAnchorEl(null);
            }}
          >
            <Paper
              sx={{
                mt: 1,
                p: 2,
                width: 180,
                borderRadius: "12px",
                boxShadow: "0px 8px 24px rgba(0,0,0,0.12)",
              }}
            >
              <TextField
                autoFocus
                value={currentValue}
                onChange={(event) => {
                  let nextValue = event.target.value.replace(/\D/g, "");

                  if (nextValue.length > 8) {
                    nextValue = nextValue.slice(0, 8);
                  }

                  setCurrentValue(nextValue);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      $
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: 40,
                    borderRadius: "10px",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#B097FF",
                      borderWidth: "1px",
                    },

                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#B097FF",
                    },

                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#B097FF",
                      borderWidth: "2px",
                    },
                  },
                }}
              />

              <Box className="button-conatiner">
                <button
                  className="btn btn-purple-compact"
                  onClick={handleSave}
                >
                  Save
                </button>

                <button
                  className="btn btn-outlined-small"
                  onClick={() => {
                    handleCancelEdit();
                    setAnchorEl(null);
                  }}
                >
                  Cancel
                </button>
              </Box>
            </Paper>
          </ClickAwayListener>
        </Popper>
      </>
    );
  }

  return renderReadOnlyText(value || "-");
};

const getColumns = (
  handlers: {
    onSave: (row: OrgManagementRow, field: "type" | "accountName" | "mrr" | "startDate" | "endDate" | "clientType", value: string) => void;
    getIsSaving?: (rowId: string, field: "type" | "accountName" | "mrr" | "startDate" | "endDate" | "clientType") => boolean;
    getHasError?: (rowId: string, field: "type" | "accountName" | "mrr"| "startDate" | "endDate" | "clientType") => boolean;
    sort?: { sort_field: string; sort_order?: "asc" | "desc" };
    onSortChange?: (field: string, order: "asc" | "desc") => void;
    domainFilter?: string;
    setDomainFilter?: (value: string) => void;
    accountNameFilter? : string;
    setAccountNameFilter?: (value: string) => void;
    relationshipTypeFilter?: string;
    setRelationshipTypeFilter?: (value: string) => void;
    openFilterColumn: string | null;
    setOpenFilterColumn: React.Dispatch<React.SetStateAction<string | null>>;
    onClearRelationshipType?: (row: OrgManagementRow) => void;
    activeUsers: any[];
    accountManagerFilter?: string[];
    setAccountManagerFilter?: (value: string[]) => void;
    onboardedDateRange?: DateRangeValue;
    setOnboardedDateRange?: (value?: DateRangeValue) => void;
    offboardedDateRange?: DateRangeValue;
    setOffboardedDateRange?: (value?: DateRangeValue) => void;
    handleAccountManagerUpdate: (row: OrgManagementRow, email: string | null) => Promise<void>
    isAdmin: boolean;
    clientTypeOptions: Options[];
    clientType?: string[];
    setClientType?: (value: string[]) => void;
  }
): MRT_ColumnDef<OrgManagementRow>[] => {
  const {
    sort,
    onSortChange,
    domainFilter = "",
    setDomainFilter,
    accountNameFilter = "",
    setAccountNameFilter,
    relationshipTypeFilter,
    setRelationshipTypeFilter,
    openFilterColumn,
    setOpenFilterColumn,
    activeUsers,
    accountManagerFilter,
    setAccountManagerFilter,
    onboardedDateRange,
    setOnboardedDateRange,
    offboardedDateRange,
    setOffboardedDateRange,
    handleAccountManagerUpdate,
    isAdmin,
    clientTypeOptions,
    clientType,
    setClientType,
  } = handlers;

  const isDomainFilterActive = openFilterColumn === "domain" || Boolean(domainFilter.trim());
  const isMrrSortActive = openFilterColumn === "mrr" || sort?.sort_field === "mrr";
  const isRelationshipTypeFilterActive = openFilterColumn === "relationshipType";
  const isAccountNameFilterActive = openFilterColumn === "accountName"|| Boolean(accountNameFilter.trim());
  const isStartDateSortActive = openFilterColumn === "onBoardedAt";
  const isEndDateSortActive = openFilterColumn === "offBoardedAt";
  const isAccountManagerFilterActive = openFilterColumn === "accountManager" || Boolean(accountManagerFilter?.length);
  const isOnboardedDateFilterActive = Boolean(
    onboardedDateRange?.startDate || onboardedDateRange?.endDate
  );
  const isOffboardedDateFilterActive = Boolean(
    offboardedDateRange?.startDate || offboardedDateRange?.endDate
  );
  const isAccountManagerSortActive = openFilterColumn === "accountManager";
  const isClientTypeFilterActive =
    openFilterColumn === "clientType" || Boolean(clientType?.length);
  const accountManagerOptions = useMemo(
    () =>
      activeUsers
        ?.filter((user) => Boolean(user?.email))
        .map((user) => ({
          label: user.name || user.email || "-",
          value: user.email,
          avatar: user.pictureUrl,
          email: user.email,
          type: "assignee" as const,
        })) || [],
    [activeUsers]
  );
  return [
  {
    accessorKey: "accountName",
    header: "",
    Header: (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}>
      <Typography
        className="cf-title" 
        sx={{ color: isAccountNameFilterActive ? "#6036d6" : "inherit"}}
      >
      Account Name </Typography>
      {sort?.sort_field === "accountName" && (
        <Box
          className="cf-sort-click"
          onClick={(_e) => {
            const newOrder =
              sort.sort_order === "asc" ? "desc" : "asc";
              onSortChange?.("accountName", newOrder);
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            marginLeft: "4px",
          }}
        >
          {sort.sort_order === "asc" ? (
            <img src={AscSortIcon} className="cf-sort-icon-active" />
          ) : (
            <img src={DescSortIcon} className="cf-sort-icon-active" />
          )}
        </Box>
      )}
      <ColumnFilter
        currentValue={accountNameFilter}
        onApply={(val) =>
          setAccountNameFilter?.(typeof val === "string" ? val.trim() : "")
        }
        options={[]}
        type="multi"
        showSections={{
          search: true,
          apply: false,
          options: false,
          sort: true,
        }}
        columnKey="accountName" 
        placeHolder={"Search by Name"}
        onOpen={() => setOpenFilterColumn("accountName")}
        onClose={() => setOpenFilterColumn(null)}
        isActive={isAccountNameFilterActive}
        sortDirection={
          sort?.sort_field === "accountName" ? sort.sort_order : null
        }
        onSortChange={onSortChange}
        enableSearchMode={true}
      />
      </Box>
    ) as unknown as string,
    size: 150,
    minSize: 150,
    maxSize: 150,
    enableSorting: false,
    Cell: ({ row }) => (
      <InlineEditor
        row={row.original}
        field="accountName"
        value={row.original.accountName === "-" ? "" : row.original.accountName}
        disabled={handlers.getIsSaving?.(row.original.id, "accountName")}
        error={handlers.getHasError?.(row.original.id, "accountName")}
        onSave={handlers.onSave}
      />
    ),
  },
  {
    accessorKey: "domain",
    header: "",
    Header: (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: openFilterColumn === "domain" ? "#F2F2F2" : "inherit",
        }}>
      <Typography
        className="cf-title" 
        sx={{ color: isDomainFilterActive ? "#6036d6" : "inherit"}}
      >
        Domain </Typography>
        {sort?.sort_field === "domain" && (
          <Box
            className="cf-sort-click"
            onClick={(_e) => {
              const newOrder =
                sort.sort_order === "asc" ? "desc" : "asc";
                onSortChange?.("domain", newOrder);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              marginLeft: "4px",
            }}
          >
            {sort.sort_order === "asc" ? (
              <img src={AscSortIcon} className="cf-sort-icon-active" />
            ) : (
              <img src={DescSortIcon} className="cf-sort-icon-active" />
            )}
          </Box>
        )}
      <ColumnFilter
        currentValue={domainFilter}
        onApply={(val) =>
          setDomainFilter?.(typeof val === "string" ? val.trim() : "")
        }
        options={[]}
        type="multi"
        showSections={{
          search: true,
          apply: false,
          options: false,
          sort: true,
        }}
        columnKey="domain" 
        placeHolder={"Search by Domain"}
        onOpen={() => setOpenFilterColumn("domain")}
        onClose={() => setOpenFilterColumn(null)}
        isActive={isDomainFilterActive}
        sortDirection={
          sort?.sort_field === "domain" ? sort.sort_order : null
        }
        onSortChange={onSortChange}
        enableSearchMode={true}
      />
    </Box>
    ) as unknown as string,
    size: 200,
    minSize: 200,
    maxSize: 200,
    enableSorting: false,
    Cell: ({ row }) => <Box>{row.original.domain || "-"}</Box>,
  },
  {
    accessorKey: "clientType",
    header: "",
    Header: (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: openFilterColumn === "clientType" ? "#F2F2F2" : "inherit",
        }}>
      <Typography
        className="cf-title" 
        sx={{ color: isClientTypeFilterActive ? "#6036d6" : "inherit"}}
      >
        Customer Type </Typography>
        {sort?.sort_field === "clientType" && (
          <Box
            className="cf-sort-click"
            onClick={(e) => {
              e.stopPropagation();
              const newOrder =
                sort.sort_order === "asc" ? "desc" : "asc";
              onSortChange?.("clientType", newOrder);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              marginLeft: "4px",
            }}
          >
            {sort.sort_order === "asc" ? (
              <img src={AscSortIcon} className="cf-sort-icon-active" />
            ) : (
              <img src={DescSortIcon} className="cf-sort-icon-active" />
            )}
          </Box>
        )}
        <ColumnFilter
          currentValue={clientType ?? []}
          onApply={(value) => {
            setClientType?.(
              Array.isArray(value) ? value.filter(Boolean) : []
            );
          }}
          options={clientTypeOptions}
          type="multi"
          showSections={{
            apply: true,
            options: true,
            sort: true,
            search: false,
          }}
          columnKey="clientType" 
          placeHolder={"Search..."}
          onOpen={() => setOpenFilterColumn("clientType")}
          onClose={() => setOpenFilterColumn(null)}
          sortDirection={
            sort?.sort_field === "clientType" ? sort.sort_order : null
          }
          onSortChange={onSortChange}
        />
      </Box>
    ) as unknown as string,
    size: 160,
    enableColumnFilter: false,
    enableSorting: false,
    minSize: 160,
    Cell: ({ row }) => (
      <InlineEditor
        row={row.original}
        field="clientType"
        value={row.original.clientType ?? "Select"}
        disabled={handlers.getIsSaving?.(
          row.original.id,
          "clientType"
        )}
        error={handlers.getHasError?.(
          row.original.id,
          "clientType"
        )}
        onSave={handlers.onSave}
        clientTypeOptions={clientTypeOptions}
      />
    )
  },
  {
    accessorKey: "type",
    header: "",
    Header: (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}>
      <Typography
        className="cf-title" 
        sx={{ color: isRelationshipTypeFilterActive ? "#6036d6" : "inherit"}}
      >
        Relationship Type </Typography>
        <ColumnFilter
        currentValue={relationshipTypeFilter}
        onApply={(val) =>
          setRelationshipTypeFilter?.(typeof val === "string" ? val.trim() : "")
        }
        options={[relationshipTypeOptions].flat()}
        type="single"
        showSections={{
          search: false,
          apply: false,
          options: true,
          sort: false,
        }}
        columnKey="relationshipType" 
        onOpen={() => setOpenFilterColumn("relationshipType")}
        onClose={() => setOpenFilterColumn(null)}
        isActive={isRelationshipTypeFilterActive}
      />
      </Box>
    ) as unknown as string,
    size: 180,
    minSize: 180,
    maxSize: 180,
    enableSorting: false,
    Cell: ({ row }) => (
      <InlineEditor
        row={row.original}
        field="type"
        value={row.original.type === "-" ? "Select" : row.original.type}
        options={RELATIONSHIP_TYPE_OPTIONS}
        disabled={handlers.getIsSaving?.(row.original.id, "type")}
        error={handlers.getHasError?.(row.original.id, "type")}
        onClearRelationshipType={handlers.onClearRelationshipType}
        onSave={handlers.onSave}
      />
    ),
  },
  {
    accessorKey: "accountManager",
    header: "",
    Header: (
      <Box className="column-header"
      sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: openFilterColumn === "accountManager" ? "#F2F2F2" : "inherit",
        }}>
        <Typography className="cf-title"
        sx={{
          color: isAccountManagerSortActive ? "#6036d6" : "inherit",
        }}
        >Account Manager</Typography>
        {sort?.sort_field === "accountManager" && (
          <Box
            className="cf-sort-click"
            onClick={(e) => {
              e.stopPropagation();
              const newOrder =
                sort.sort_order === "asc" ? "desc" : "asc";
              onSortChange?.("accountManager", newOrder);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              marginLeft: "4px",
            }}
          >
            {sort.sort_order === "asc" ? (
              <img src={AscSortIcon} className="cf-sort-icon-active"
                />
              ) : (
                <img src={DescSortIcon} className="cf-sort-icon-active"
                />
              )}
          </Box>
        )}
        <ColumnFilter
          currentValue={accountManagerFilter ?? []}
          onApply={(value) => {
            setAccountManagerFilter?.(
              Array.isArray(value) ? value.filter(Boolean) : []
            );
          }}
          options={accountManagerOptions}
          type="multi"
          showSections={{
            search: true,
            options: true,
            apply: true,
            sort: true,
          }}
          columnKey="accountManager"
          placeHolder="Search manager"
          sortDirection={
            sort?.sort_field === "accountManager"
              ? (sort.sort_order as "asc" | "desc")
              : null
          }
          onSortChange={onSortChange}
          onClose={() => setOpenFilterColumn(null)}
          isActive={isAccountManagerFilterActive}
        />
      </Box>
      ) as unknown as string,
    size: 180,
    minSize: 180,
    maxSize: 200,
    enableSorting: false,
    Cell: ({ row }) => {
      const selectedManager =
        activeUsers?.find((user) => user.email === row.original.accountManagerEmail) ||
        {
          name: row.original.accountManagerName,
          email: row.original.accountManagerEmail,
        };

      return (
        <ActionItemsAssigneeCell
          email={selectedManager?.email || row.original.accountManagerEmail || null}
          name={selectedManager?.name || row.original.accountManagerName || "-"}
          participant={selectedManager}
          attendeeParticipants={activeUsers}
          canEdit={isAdmin}
          row={row}
          showCrossIcon={isAdmin}
          showEditIcon={isAdmin}
          showName={true}
          dashed={isAdmin}
          hideActionIcon={true}
          updateAssigneeKey="email"
          rowAction={{
            updateAssigneeCallback: (
              rowData: any,
              selectedManagerEmail: string | null
            ) => {
              handleAccountManagerUpdate(
                rowData.original,
                selectedManagerEmail
              );
            },
          }}
        />
      );
    },
  },
  {
    accessorKey: "mrr",
    header: "",
    Header: (
      <Box className="column-header"
      sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: openFilterColumn === "mrr" ? "#F2F2F2" : "inherit",
        }}>
        <Typography className="cf-title"
        sx={{
          color: isMrrSortActive ? "#6036d6" : "inherit",
        }}
        >MRR</Typography>
        {sort?.sort_field === "mrr" && (
          <Box
            className="cf-sort-click"
            onClick={(e) => {
              e.stopPropagation();
              const newOrder =
                sort.sort_order === "asc" ? "desc" : "asc";
              onSortChange?.("mrr", newOrder);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              marginLeft: "4px",
            }}
          >
            {sort.sort_order === "asc" ? (
              <img src={AscSortIcon} className="cf-sort-icon-active"
                />
              ) : (
                <img src={DescSortIcon} className="cf-sort-icon-active"
                />
              )}
          </Box>
        )}

        <ColumnFilter
          currentValue=""
          onApply={() => undefined}
          options={[]}
          showSections={{
            sort: true,
            search: false,
            options: false,
            apply: false,
          }}
          onOpen={() => setOpenFilterColumn("mrr")}
          type="single"
          columnKey="mrr"
          sortDirection={
            sort?.sort_field === "mrr"
              ? (sort.sort_order as "asc" | "desc")
              : null
          }
          onSortChange={onSortChange}
          onClose={() => setOpenFilterColumn(null)}
          isActive={isMrrSortActive}
        />
      </Box>
      ) as unknown as string,
    size: 100,
    minSize: 100,
    maxSize: 100,
    enableSorting: false,
    Cell: ({ row }) => (
      <InlineEditor
        row={row.original}
        field="mrr"
        value={
          row.original.mrr === "-" || row.original.mrr === null || row.original.mrr === undefined
            ? ""
            : String(row.original.mrr)
        }
        disabled={handlers.getIsSaving?.(row.original.id, "mrr")}
        error={handlers.getHasError?.(row.original.id, "mrr")}
        onSave={handlers.onSave}
      />
    ),
  },
  {
    accessorKey: "onBoardedAt",
    header: "",
    Header: (
      <Box
        className="column-header"
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: openFilterColumn === "onBoardedAt" ? "#F2F2F2" : "inherit",
        }}
      >
        <Typography
          className="cf-title"
          sx={{
            color:
              isStartDateSortActive || isOnboardedDateFilterActive
                ? "#6036d6"
                : "inherit",
          }}
        >
          Onboarded Date
        </Typography>
        {sort?.sort_field === "onBoardedAt" && (
          <Box
            className="cf-sort-click"
            onClick={(e) => {
              e.stopPropagation();
              const newOrder =
                sort.sort_order === "asc" ? "desc" : "asc";
              onSortChange?.("onBoardedAt", newOrder);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              marginLeft: "4px",
            }}
          >
            {sort.sort_order === "asc" ? (
              <img src={AscSortIcon} className="cf-sort-icon-active"
                />
              ) : (
                <img src={DescSortIcon} className="cf-sort-icon-active"
                />
              )}
          </Box>
        )}
        <ColumnFilter
          currentValue={onboardedDateRange}
          onApply={(value) => {
            if (!value || typeof value === "string" || Array.isArray(value)) {
              setOnboardedDateRange?.(undefined);
              return;
            }
            setOnboardedDateRange?.(value as DateRangeValue);
          }}
          options={[]}
          type="date"
          showSections={{
            sort: true,
          }}
          columnKey="onBoardedAt"
          sortDirection={
            sort?.sort_field === "onBoardedAt"
              ? (sort.sort_order as "asc" | "desc")
              : null
          }
          onSortChange={onSortChange}
          onOpen={() => setOpenFilterColumn("onBoardedAt")}
          onClose={() => setOpenFilterColumn(null)}
          isActive={isOnboardedDateFilterActive}
        />
      </Box>
      ) as unknown as string,
    size: 150,
    minSize: 150,
    maxSize: 150,
    enableSorting: false,
    Cell: ({ row }) => (
      <DateCellEditor
        row={row.original}
        field="startDate"
        value={row.original.startDate}
        canEdit={isAdmin}
        onSave={handlers.onSave}
      />
    ),
  },
  {
    accessorKey: "offBoardedAt",
    header: "",
    Header: (
      <Box
        className="column-header"
        sx={{
          display: "flex",
          alignItems: "center",
          backgroundColor: openFilterColumn === "offBoardedAt" ? "#F2F2F2" : "inherit",
        }}
      >
        <Typography
          className="cf-title"
          sx={{
            color:
              isEndDateSortActive || isOffboardedDateFilterActive
                ? "#6036d6"
                : "inherit",
          }}
        >
          Off boarding Date
        </Typography>
        {sort?.sort_field === "offBoardedAt" && (
          <Box
            className="cf-sort-click"
            onClick={(e) => {
              e.stopPropagation();
              const newOrder =
                sort.sort_order === "asc" ? "desc" : "asc";
              onSortChange?.("offBoardedAt", newOrder);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              marginLeft: "4px",
            }}
          >
            {sort.sort_order === "asc" ? (
              <img src={AscSortIcon} className="cf-sort-icon-active"
                />
              ) : (
                <img src={DescSortIcon} className="cf-sort-icon-active"
                />
              )}
          </Box>
        )}
        <ColumnFilter
          currentValue={offboardedDateRange}
          onApply={(value) => {
            if (!value || typeof value === "string" || Array.isArray(value)) {
              setOffboardedDateRange?.(undefined);
              return;
            }

            setOffboardedDateRange?.(value as DateRangeValue);
          }}
          options={[]}
          type="date"
          showSections={{
            sort: true,
          }}
          columnKey="offBoardedAt"
          sortDirection={
            sort?.sort_field === "offBoardedAt"
              ? (sort.sort_order as "asc" | "desc")
              : null
          }
          onSortChange={onSortChange}
          onOpen={() => setOpenFilterColumn("offBoardedAt")}
          onClose={() => setOpenFilterColumn(null)}
          isActive={isOffboardedDateFilterActive}
        />
      </Box>
      ) as unknown as string,
    size: 160,
    minSize: 160,
    maxSize: 160,
    enableSorting: false,
    Cell: ({ row }) => (
      <DateCellEditor
        row={row.original}
        field="endDate"
        value={row.original.endDate}
        canEdit={isAdmin}
        onSave={handlers.onSave}
      />
    ),
  },
  ];
};

export { getColumns, initialSort };
